import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentProps,
  type FormEvent,
  type ReactNode,
  type SyntheticEvent,
} from 'react';
import { ApiError } from '../api';
import { Button, Dialog } from '../ui';
import { FieldErrorContext } from '../ui/fieldErrors';
import { useConsoleI18n } from './bind';
import { useConfirm } from './ConfirmDialog';
import { ErrorBox } from './components';
import { consoleEn, type ConsoleTextKey } from './en';

// One dialog pattern for creating and editing (`docs/ui-kit.md` X4, from nextgen's MX ADR 0128): size tokens instead
// of free widths; Cancel, the close button, Escape and the backdrop ask before throwing away unsaved input (the
// `draft` compared with how it opened, else what was typed or picked in it), and none of them closes while the command
// runs; the submit button belongs to the form (`form=`), so Enter in a field submits; a concurrency conflict (412, or
// a 409 with a concurrency code) shows a banner with a reload, any other problem its own text; validation errors of
// the problem's `errors` reach the fields that name them (`name=` on a kit field, or `useFieldError`).

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen';

/** Width of each size token; `fullscreen` takes the viewport less its margin. */
export const DIALOG_WIDTH: Record<DialogSize, string> = {
  sm: '480px',
  md: '640px',
  lg: '820px',
  xl: '1100px',
  fullscreen: 'calc(100vw - 32px)',
};

/** The size token nearest to a pixel width (dialogs that still pass `maxWidth`). */
export const sizeOf = (maxWidth?: number | string): DialogSize =>
  typeof maxWidth !== 'number'
    ? 'md'
    : maxWidth <= 520
      ? 'sm'
      : maxWidth <= 700
        ? 'md'
        : maxWidth <= 920
          ? 'lg'
          : 'xl';

/** The kit's texts through the app's catalogue, English when the app has no such key. */
function useKitTexts() {
  const { t } = useConsoleI18n();
  return (key: ConsoleTextKey) => {
    const text = t(key);
    return text === key ? consoleEn[key] : text;
  };
}

interface DialogContextValue {
  /** Closes the dialog, asking first when there is unsaved input. */
  close: () => void;
  /** The id of the dialog's form (`FormDialog`), for `form=` on buttons outside it. */
  formId?: string;
  /** The current problem, for `useFieldError`. */
  error?: unknown;
  /** A `DialogError` reports the problem it shows (dialogs that render it themselves). */
  report?: (error: unknown) => void;
  /** The command runs: the dialog does not close. */
  busy?: boolean;
}

const DialogContext = createContext<DialogContextValue | null>(null);

/** The open dialog's guarded close (for a Cancel button the caller renders itself). */
export const useDialogClose = () => useContext(DialogContext)?.close;

/**
 * A field's validation message from the dialog's problem (`errors` keyed by field name, matched case-insensitively and
 * by the last path segment: `lines[0].quantity` answers `quantity`).
 */
export function useFieldError(name: string): string | undefined {
  return fieldError(useContext(DialogContext)?.error, name);
}

export function fieldError(error: unknown, name: string): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const errors = (error.body as { errors?: unknown } | undefined)?.errors;
  if (!errors || Array.isArray(errors) || typeof errors !== 'object') return undefined;
  const want = name.toLowerCase();
  for (const [key, value] of Object.entries(errors as Record<string, string[] | string>)) {
    const k = key.toLowerCase();
    if (k === want || k.endsWith(`.${want}`) || k.replace(/\[\d+\]/g, '') === want)
      return Array.isArray(value) ? value.join(' ') : value;
  }
  return undefined;
}

/**
 * Problem codes of a 409 that mean "the record changed since you read it"; an app adds its own. Other 409s are
 * business refusals and keep their text.
 */
export const CONCURRENCY_CODES = new Set(['version-conflict', 'precondition-failed']);

/** A concurrency conflict: a stale `If-Match` (412) or a 409 with a concurrency code. */
export const isConflict = (error: unknown) =>
  error instanceof ApiError &&
  (error.status === 412 || (error.status === 409 && !!error.code && CONCURRENCY_CODES.has(error.code)));

export interface DialogFrameProps extends Omit<ComponentProps<typeof Dialog>, 'maxWidth' | 'open'> {
  open?: boolean;
  size?: DialogSize;
  /** A pixel width, mapped to the nearest size token (prefer `size`). */
  maxWidth?: number | string;
  /** Unsaved input: closing asks first (overrides `draft`). */
  dirty?: boolean;
  /**
   * The form's draft: unsaved when it differs from the draft the dialog first had (`undefined` while it loads: the
   * first defined draft is the baseline). Without `draft` or `dirty` the dialog counts what was typed or picked in it.
   */
  draft?: unknown;
  /** The version the draft was made from: a new one (the record reloaded after a conflict) is a new baseline. */
  draftVersion?: string | number;
  /** The command runs: Escape, the close button, the backdrop and Cancel do nothing. */
  busy?: boolean;
}

const snapshot = (v: unknown) => JSON.stringify(v ?? null);

/** Controls whose activation changes a form without an `input`/`change` event (switches, options, chips). */
const PICKS =
  '[role="switch"], [role="option"], [role="checkbox"], [role="radio"], [aria-pressed], .nyk-ui-chip-remove, [data-dirties]';

/** Whether an event in the dialog's body is an edit (not a search box, nor anything marked `data-nodirty`). */
function isEdit(e: SyntheticEvent) {
  const el = e.target as Element | null;
  if (!el?.closest || el.closest('[data-nodirty]')) return false;
  if (el instanceof HTMLInputElement && el.type === 'search') return false;
  // typing into a combobox is a query; a pick is counted from the option
  if (el.getAttribute('role') === 'combobox') return false;
  return true;
}

/**
 * The kit Dialog with size tokens and a guarded close: Escape, the backdrop and the close button ask "Discard your
 * changes?" once something was typed (or `dirty`), and a backdrop press never closes a dialog with input.
 * `useDialogClose()` gives a Cancel button the same guarded close.
 */
export function DialogFrame({
  open = true,
  size,
  maxWidth,
  dirty,
  draft,
  draftVersion,
  busy,
  onClose,
  closeLabel,
  closeOnBackdrop,
  children,
  ...rest
}: DialogFrameProps) {
  const kt = useKitTexts();
  const confirm = useConfirm();
  const typed = useRef(false);
  const ask = useRef(false);
  const baseline = useRef<string | undefined>(undefined);
  const baseVersion = useRef(draftVersion);
  // a closed dialog forgets its baseline: reopened, it compares with the draft it opens with
  if (!open || draft === undefined || baseVersion.current !== draftVersion) {
    baseline.current = undefined;
    baseVersion.current = draftVersion;
  }
  if (open && draft !== undefined && baseline.current === undefined) baseline.current = snapshot(draft);
  const context = useContext(DialogContext);
  const running = busy ?? context?.busy ?? false;
  const unsaved = () =>
    dirty ??
    (draft !== undefined && baseline.current !== undefined
      ? snapshot(draft) !== baseline.current
      : typed.current);
  const [reported, setReported] = useState<unknown>(undefined);
  const error = context?.error ?? reported;
  const close = useCallback(async () => {
    if (ask.current || running) return;
    if (unsaved()) {
      ask.current = true;
      const discard = await confirm({
        title: kt('dirty.title'),
        message: kt('dirty.message'),
        confirmLabel: kt('dirty.discard'),
        danger: true,
      });
      ask.current = false;
      if (!discard) return;
    }
    typed.current = false;
    onClose();
    // `unsaved` reads refs, `dirty` and `draft`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirm, onClose, dirty, draft, running]);
  return (
    // The provider wraps the whole dialog: a Cancel in the footer gets the same guarded close as the close button.
    <DialogContext.Provider
      value={{ ...context, close: () => void close(), error, report: setReported, busy: running }}
    >
      <FieldErrorContext.Provider value={(name) => fieldError(error, name)}>
        <Dialog
          {...rest}
          open={open}
          onClose={() => void close()}
          closeLabel={closeLabel ?? kt('dialog.close')}
          closeOnBackdrop={closeOnBackdrop ?? false}
          maxWidth={DIALOG_WIDTH[size ?? sizeOf(maxWidth)]}
        >
          <div
            className="nyk-dialog-content"
            onInput={(e) => isEdit(e) && (typed.current = true)}
            onChange={(e) => isEdit(e) && (typed.current = true)}
            onClick={(e) => {
              const pick = (e.target as Element).closest?.(PICKS);
              if (pick && isEdit(e) && !pick.closest('[aria-disabled="true"], :disabled'))
                typed.current = true;
            }}
          >
            {children}
          </div>
        </Dialog>
      </FieldErrorContext.Provider>
    </DialogContext.Provider>
  );
}

/** Cancel for a dialog footer: the dialog's guarded close. */
export function DialogCancel({
  children,
  onClick,
  disabled,
}: {
  children?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  const context = useContext(DialogContext);
  const kt = useKitTexts();
  return (
    <Button
      variant="ghost"
      type="button"
      disabled={disabled || context?.busy}
      onClick={onClick ?? context?.close}
    >
      {children ?? kt('dialog.cancel')}
    </Button>
  );
}

/**
 * The problem of a dialog's last command: a concurrency conflict as "changed by someone else" with a reload
 * (`onReload`: the record again in place; else the page), anything else as the kit's ErrorBox. It reports the problem
 * to its dialog, so the fields it names show their messages.
 */
export function DialogError({ error, onReload }: { error: unknown; onReload?: () => void }) {
  const kt = useKitTexts();
  const report = useContext(DialogContext)?.report;
  useEffect(() => {
    report?.(error ?? undefined);
  }, [report, error]);
  if (!error) return null;
  if (isConflict(error))
    return (
      <div className="nyk-ui-login-error nyk-ui-connection nyk-error" role="alert">
        <div>
          <b>{kt('dialog.conflict')}</b>
          <div>{kt('dialog.conflictDetail')}</div>
        </div>
        <Button size="sm" variant="ghost" onClick={onReload ?? (() => window.location.reload())}>
          {kt('dialog.reload')}
        </Button>
      </div>
    );
  return <ErrorBox error={error} />;
}

export interface FormDialogProps extends Omit<DialogFrameProps, 'footer' | 'children'> {
  children: ReactNode;
  /** Runs on submit (Enter in a field or the submit button). */
  onSubmit: () => void;
  submitLabel?: ReactNode;
  /** The submit button is disabled (the form is incomplete); Enter does nothing then either. */
  canSubmit?: boolean;
  busy?: boolean;
  /** Red submit (delete, revoke). */
  danger?: boolean;
  /** The last command's problem: shown on top, its field errors on the fields that name them. */
  error?: unknown;
  /** After a conflict: reload the record (default: the page). */
  onReload?: () => void;
  /** More footer buttons, left of Cancel (e.g. "Delete"). */
  extraActions?: ReactNode;
  /** Classes of the form (default `nyk-form`, the two-column field grid). */
  formClassName?: string;
}

/**
 * The create/edit dialog: the problem on top, the form, Cancel and the submit in the footer. The submit button is
 * linked to the form (`form=`), so it works from the footer and Enter submits.
 */
export function FormDialog({
  children,
  onSubmit,
  submitLabel,
  canSubmit = true,
  busy,
  danger,
  error,
  onReload,
  extraActions,
  formClassName = 'nyk-form',
  ...frame
}: FormDialogProps) {
  const kt = useKitTexts();
  const formId = useId();
  return (
    <DialogContext.Provider value={{ close: frame.onClose, formId, error, busy }}>
      <DialogFrame
        {...frame}
        footer={
          <>
            {extraActions}
            <DialogCancel />
            <Button
              type="submit"
              form={formId}
              loading={busy}
              disabled={!canSubmit}
              variant={danger ? 'danger' : undefined}
            >
              {submitLabel ?? kt('dialog.save')}
            </Button>
          </>
        }
      >
        <DialogError error={error} onReload={onReload} />
        <form
          id={formId}
          className={formClassName}
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmit || busy) return;
            onSubmit();
          }}
        >
          {children}
        </form>
      </DialogFrame>
    </DialogContext.Provider>
  );
}

/**
 * The submit handler of a dialog's own form (`<form id onSubmit={submitWith(save)}>` with the footer's submit button
 * linked by `form=`): runs only when an enabled submit button submitted it, so Enter can never do what the disabled
 * (or absent) button would not.
 */
export function submitWith(run: () => unknown) {
  return (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const by = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null;
    if (!by || by.disabled) return;
    void run();
  };
}
